import { isDefined } from '@sgme/fp';
import classNames from 'classnames';
import { RenderFormNodeComponent } from 'components/common-form/builder/form/FormBuilder';
import { DataApi, DataContext } from 'components/common-form/contexts/DataContext';
import { FieldMetadataContext } from 'components/common-form/contexts/FieldMetadataContext';
import { FormOrientationContext } from 'components/common-form/contexts/FormOrientationContext';
import { NumericFieldsContext } from 'components/common-form/contexts/NumericFieldsContext';
import { RemovableElementContext } from 'components/common-form/contexts/RemovableElementContext';
import { UpdateActionContext } from 'components/common-form/contexts/UpdateActionContext';
import { HorizontalDivider } from 'components/shared/Divider';
import type { TagItem } from 'components/shared/Tags';
import { SgTagsPicker } from 'components/shared/Tags';
import {
  connectMultipleApply,
  type MultipleApplyProps,
} from 'components/Workspaces/Batch/MultipleApply/MultipleApply.connect';
import type { IntlKey } from 'locales';
import { useMemo } from 'react';
import { Modal, ModalBody, ModalFooter, ModalHeader, ModalTitle } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import { camelCaseToWords } from 'utils/string';

function Placeholder() {
  return (
    <div className="d-flex justify-content-center flex-column my-auto text-secondary">
      <i className="icon icon-lg text-center">playlist_add_check</i>
      <h4 className="text-center">
        <FormattedMessage id="batchPricer.modal.placeholder.title" />
      </h4>
      <span className=" text-center">
        <FormattedMessage id="batchPricer.modal.placeholder.hint" />
      </span>
    </div>
  );
}
export function MultipleApply({
  tabId,
  isMultipleApplyOpened,
  onClose,
  onApply,
  fieldsMetadata,
  fieldsNodes,
  fieldsData,
  setFieldValue,
  setSelectedFieldsIds,
  selectedFieldsIds,
  formsCnt,
  fieldsActions,
  setFieldAction,
  isApplyButtonDisabled,
}: MultipleApplyProps) {
  const { formatMessage } = useIntl();
  const fieldsPickerPlaceholder = formatMessage({ id: 'batchPricer.modal.fieldsPicker' });
  const dataApi: DataApi = useMemo(() => ({ setFieldValue }), [setFieldValue]);
  const hasFields = selectedFieldsIds.length > 0;
  const formattedFieldsIds: TagItem[] = useMemo(
    () =>
      Object.values(fieldsMetadata)
        ?.map(field => {
          const id = field.translationKey ?? field.id;
          return {
            key: field.id,
            label: formatMessage({
              id: `form.${id}` as IntlKey,
              defaultMessage: camelCaseToWords(id),
            }),
          };
        })
        .filter(isDefined) ?? [],
    [fieldsMetadata, formatMessage],
  );
  const removeField = (field: string) => setSelectedFieldsIds(selectedFieldsIds.filter(id => id !== field));
  return (
    <Modal
      show={isMultipleApplyOpened}
      onHide={onClose}
      size="lg"
      contentClassName="h-50"
      keyboard={false}
      backdrop="static"
      data-e2e="multiple-apply-modal"
    >
      <ModalHeader className="pb-2" closeButton>
        <ModalTitle as="h3">
          <FormattedMessage id="batchPricer.apply" />
        </ModalTitle>
      </ModalHeader>
      <ModalBody className="overflow-y-auto">
        <div className={classNames('d-flex flex-column', { 'h-100': !hasFields })}>
          <SgTagsPicker
            id="fields-picker"
            className="mb-4"
            onSelectionKeysChanged={setSelectedFieldsIds}
            icon="tune"
            summary
            size="xl"
            selection={selectedFieldsIds}
            available={formattedFieldsIds}
            disabled={false}
            placeholder={fieldsPickerPlaceholder}
            hasTooltip
            singleSelect={false}
          ></SgTagsPicker>
          {!hasFields && <Placeholder />}

          <NumericFieldsContext.Provider value={{ useIncrementers: true, incrementersLayout: 'end' }}>
            <RemovableElementContext.Provider value={{ onRemove: removeField }}>
              <FormOrientationContext.Provider value="removableListItem">
                <FieldMetadataContext.Provider value={fieldsMetadata}>
                  <UpdateActionContext.Provider value={[fieldsActions, setFieldAction]}>
                    <DataContext.Provider value={[fieldsData, dataApi]}>
                      {fieldsNodes.map(n => (
                        <div key={n.id}>
                          <HorizontalDivider />
                          <div className="d-flex w-100 align-items-center p-1">
                            <RenderFormNodeComponent
                              key={n.id}
                              tabId={tabId}
                              level={1}
                              workspaceType="newBatch"
                              quoteWorkspaceVersion="Advanced"
                              batchWorkspaceVersion="Advanced"
                              node={n}
                              mustForceAdvancedMode
                              isMultipleApply
                            />
                          </div>
                        </div>
                      ))}
                    </DataContext.Provider>
                  </UpdateActionContext.Provider>
                </FieldMetadataContext.Provider>
              </FormOrientationContext.Provider>
            </RemovableElementContext.Provider>
          </NumericFieldsContext.Provider>
        </div>
      </ModalBody>
      <ModalFooter>
        <button className="btn btn-xl btn-flat-secondary border" data-e2e="modal-cancel-button" onClick={onClose}>
          <FormattedMessage id="discard" />
        </button>
        <button
          onClick={onApply}
          disabled={isApplyButtonDisabled}
          className="btn btn-xl btn-primary "
          data-e2e="modal-ok-button"
        >
          <FormattedMessage id="batchPricer.modal.applyToQuotes" values={{ quotesCnt: formsCnt }} />
        </button>
      </ModalFooter>
    </Modal>
  );
}

export const ConnectedMultipleApply = connectMultipleApply(MultipleApply);
